<template>
  <div class="panel panel-objective" :class="panelClass">
    <CopyTextWrapper type="objective" :level="level">
      <ObjectiveHeading
        :level="level"
        :title="title"
        :show-quarter="false"/>
      <div class="panel-body">
        <div class="gb-text-2">
          <slot name="body"/>
        </div>
        <template v-if="items && items.length > 0">
          <br>
          <button
            class="btn btn-default"
            :class="levelButtonClass"
            @click="toggleItemModal(0, true)">
            <div class="pull-left">
              <i class="fa fa-file fa-2x"/>
            </div>
            <div class="btn-content">
              <span class="main">View Student Task</span>
              <small class="subtext">Downloadable resources included</small>
            </div>
          </button>
          <ItemModal
            :item-id="itemId"
            :level="level"
            :title="itemModalTitle"
            @update:show="toggleItemModal(0, $event)"
            @print="togglePrintView(true)"
            :show="showItemModalState[0]"
            :access-info="accessInfo">
            <div slot="objective">
              <slot name="objective"/>
            </div>
          </ItemModal>

          <PrintableObjective
            v-if="showPrintableOverlay"
            @update:show="togglePrintView($event)"
            @print="onPrint"
            :item-id="itemId"
            :objective-level="level"
            :objective-title="title"
            :objective-text="text"/>

          <LimitActionModal :show.sync="showBlocker" :action-permissions="printPermissions" />
        </template>
        <div>
          <slot name="footer"/>
        </div>
      </div>
    </CopyTextWrapper>
  </div>
</template>

<script>
import CopyTextWrapper from '@/components/CopyTrackWrapper'
import ObjectiveHeading from '@/components/goals/ObjectiveHeading'
import ItemModal from '@/components/goals/ItemModal'
import PrintableObjective from '@/components/goals/PrintableObjective'
import LimitActionModal from '@/components/LimitActionModal'
import { isValidObjectiveLevel, levelButtonClass } from '@/components/goals/common/objectives'
import { mapActions } from 'vuex'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { ObjectiveLevelValues } from '@/components/goals/common/objectives'

export default {
  name: 'Objective',
  components: {
    CopyTextWrapper,
    ItemModal,
    ObjectiveHeading,
    PrintableObjective,
    LimitActionModal
  },
  props: {
    level: {
      type: String,
      required: true,
      validator: isValidObjectiveLevel
    },
    title: {
      type: String,
      default: 'Measurable Objective'
    },
    text: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default() {
        return []
      }
    },
    // this prop is basically just for inclusion in analytics...
    pageUuid: {
      type: String,
      default: ''
    },
    accessInfo: {
      type: Object,
      required: true
    },
    printPermissions: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showItemModalState: {},
      showPrintableOverlay: false,
      showBlocker: false
    }
  },
  eventContext() {
    return {
      obj_level: this.level,
      access: !this.printPermissions.block
    }
  },
  computed: {
    panelClass() {
      return {
        'quarter-1': this.level === ObjectiveLevelValues.QUARTER_1ST,
        'quarter-2': this.level === ObjectiveLevelValues.QUARTER_2ND,
        'quarter-3': this.level === ObjectiveLevelValues.QUARTER_3RD,
        'quarter-4': this.level === ObjectiveLevelValues.QUARTER_4TH
      }
    },
    levelButtonClass() {
      return levelButtonClass(this.level)
    },
    itemModalTitle() {
      return this.title
    },
    itemId() {
      return this.items[0]
    }
  },
  methods: {
    toggleItemModal(itemIndex, flag) {
      this.$set(this.showItemModalState, itemIndex, flag)

      if (flag) {
        this.$sendEvent(
          'Clicked Open Assessment',
          {},
          {
            cu: this.pageUuid,
            ct: this.title
          }
        )
      }

      if (!flag) {
        // if we're dismissing the modal, also make sure to dismiss print view if it's open
        this.togglePrintView(false)
      }
    },
    togglePrintView(flag) {
      if (flag) {
        this.$sendEvent('Clicked Print Objective')
      }
      if (flag && this.printPermissions.block) {
        this.showBlocker = true
        return
      }
      this.showPrintableOverlay = flag
    },
    onPrint(printOptions) {
      this.trackPrint({
        contentType: CONTENT_TYPES.SUPER_PAGE_ASSESSMENT,
        contentTitle: this.title,
        contentUuid: this.itemId,
        otherData: {
          superpage_uuid: this.pageUuid,
          obj_level: this.level,
          print_options: printOptions
        }
      })
    },
    ...mapActions('contentAccess', ['trackPrint'])
  }
}
</script>

<style scoped lang="less">
@import '~@/variables.less';

.panel-objective {
  margin-bottom: 28px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  .btn {
    font-weight: 300;
    border: 0;
    padding: 8px 14px;
    padding-right: 16px;

    @iconWidth: 24px;
    .pull-left {
      width: @iconWidth;
      margin-top: 3px;
      color: white;
    }
    .btn-content {
      padding-left: @iconWidth + 8;
    }
    .main,
    .subtext {
      display: block;
      text-align: left;
      letter-spacing: 0.3px;
    }
    .main {
      text-transform: uppercase;
      color: white;
      font-weight: 500;
    }
    .subtext {
      color: #ececec;
      font-weight: 300;
    }
    &.btn-teal {
      background-color: @tealObjective;
      &:hover {
        background-color: darken(@tealObjective, 7%);
      }
    }
    &.btn-blue {
      background-color: @blueObjective;
      &:hover {
        background-color: darken(@blueObjective, 7%);
      }
    }
    &.btn-purple {
      background-color: @purpleObjective;
      &:hover {
        background-color: darken(@purpleObjective, 7%);
      }
    }

  }
  &.quarter-1 {
    border-color: @tealObjective;
  }
  &.quarter-2 {
    border-color: @blueObjective;
  }
  &.quarter-3 {
    border-color: @purpleObjective;
  }
}
</style>
